import BlockHoverMenu from '@/components/editor/BlockHoverMenu';
import InternalLinkNode from '@/components/editor/nodeViews/InternalLinkNode';
import MediaResize from '@/components/editor/nodeViews/MediaResize';
import MentionNode from '@/components/editor/nodeViews/MentionNode';
import StImagePlaceholderNodeView from '@/components/editor/nodeViews/StImagePlaceholder';
import LinkTooltip from '@/components/editor/popups/LinkTooltip';
import constants from '@/utilities/constants';
import useDisplayImageFullScreen from '@/utilities/composables/useDisplayImageFullScreen';
import EventBus from '@/utilities/eventBus';
import getFileIdFromUrl from '@/utilities/getFileIdFromUrl';
import getTextNodeFromPoint from '@/utilities/getTextNodeFromPoint';
import { isNotInternalLink, getInternalLinkProps } from '@/utilities/internalLink';
import Comment from '@superthread-com/extension-comment/src/index';
import StImage from '@superthread-com/extension-image/src/index';
import StImagePlaceholder from '@superthread-com/extension-image-placeholder/src/index';
import InternalLink from '@superthread-com/extension-internal-link/src/index';
import CustomUserMention from '@superthread-com/extension-mention/src/index';
import { HorizontalRuleOptions, HorizontalRule } from '@tiptap/extension-horizontal-rule';
import PreCommentHighlight from '@/components/editor/plugins/PrecommentHighlight';
import TaskItem from '@superthread-com/extension-task-item/src/index';
import { ListItem } from '@tiptap/extension-list-item';
import Youtube from '@tiptap/extension-youtube';
import UniqueId from '@tiptap-pro/extension-unique-id';
import { Editor, EditorOptions, VueNodeViewRenderer, findChildren } from '@tiptap/vue-3';
import InternalLinkRenderExtend from '../helpers/InternalLinkRenderExtend';
import Code from '@tiptap/extension-code';
import { Collaboration, isChangeOrigin } from '@tiptap/extension-collaboration';
import CollaborationCursor from '@tiptap/extension-collaboration-cursor';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import TaskList from '@tiptap/extension-task-list';
import Typography from '@tiptap/extension-typography';
import Underline from '@tiptap/extension-underline';
import StarterKit from '@tiptap/starter-kit';
import { nanoid } from 'nanoid';
import type { Awareness } from 'y-protocols/awareness';
import { Doc } from 'yjs';
import { ImageUploadExtension } from '../customExtensions/ImageUploadExtension';
import Resizing from '../customExtensions/Resizing';
import { MouseOver } from '../customExtensions/mouseOver';
import { objectByImgSrc, unsplashObjectByImgSrc } from '../helpers/generateImageObject';
import { replaceHandlePastePlugin } from '../helpers/replaceHandlePastePlugin';
import { replaceLinkClickPlugin } from '../helpers/replaceLinkClickPlugin';
import { mergeCellsDisabled } from '../helpers/mergeCellsDisabled';
import { uploadFn } from '../helpers/uploadFile';
import { autolink } from '../plugins/InternalLinkAutolink';
import { doubleClick } from '../plugins/doubleClick';
import { EventHandler } from '../plugins/eventHandler';
import { getFileCache, imageUploader } from '../plugins/fileReadUpload';
import userMentionSuggestion from './lib/userMentionSuggestion';
import { renderCollaborationCursors } from '../helpers/collaborationCursor';
import { PhantomPlugin } from '../plugins/phantomSuggester';
import TablePlugins from '@superthread-com/extension-table/src/index';
import '@superthread-com/extension-table/src/style.scss';
import '@superthread-com/extension-inline-media/src/style.scss';
import { TableOfContent } from '../customExtensions/tableOfContents';
import { CharacterCount } from '@/components/editor/plugins/characterCount';
import CustomEnter from '../plugins/customEnter';
import { InlineMediaNode } from '@superthread-com/extension-inline-media/src/index';
import { GenericMediaNode, GenericMediaPlacholder } from '../plugins/inlineMedia';
import FilterFiles from '../plugins/filterFiles';
import { ResourceType } from '@/types/resources';
import customShortcuts from '../plugins/customShortcuts';
import TextAlign from '@tiptap/extension-text-align';
import EmbedBoard from '@superthread-com/extension-board-embed/src/index';
import BoardEmbed from '@/components/editor/nodeViews/BoardEmbed.vue';
import ContentMenu from '@/components/editor/plugins/ContentMenu';
import Callouts from '@superthread-com/extension-callouts/src';
import Callout from '@/components/editor/nodeViews/Callout.vue';
import CodeBlockLowlight from '@tiptap/extension-code-block-lowlight';
import { createLowlight, common } from 'lowlight';
import exitBlockAbove from '@/components/editor/customCommnads/exitBlockAbove';
import { reactive, Component } from 'vue';
import { isInTable } from '@tiptap/pm/tables';
import '@/utilities/setParagraphWhiteSpace';
import BlockNodeView from '@/components/editor/nodeViews/BlockNodeView.vue';
import Blockquote from '@tiptap/extension-blockquote';

export interface User {
  name: string;
  color: string;
  yjsId: number;
}

export interface CollaborationOptions {
  document: Doc;
  awareness: Awareness;
  user: User;
}

export interface EditorServiceOptions {
  resourceId: string;
  resourceType: ResourceType;
  parentResourceType: string;
  parentResourceId: string;
  placeholder?: string;
  commenting?: boolean;
  draggable?: boolean;
  heading?: boolean;
  allowImages: boolean;
  allowImagesUpload: boolean;
  lists: boolean;
  handleArrowLeft: boolean;
  horizontalRule?: Partial<HorizontalRuleOptions> | false;
  blockElements?: boolean;
  collaboration?: CollaborationOptions;
  focusedStatePlaceholder: string;
  tableOfContent: boolean;
  editorDomRef: HTMLElement;
  preventInsertFiles: boolean;
  characterLimit?: number;
  highlight?: boolean;
  textAlignment?: boolean;
  enableContentMenu?: boolean;
  enableBoardEmbed?: boolean;
  storeDispatch: any;
}

const ydocFragmentField = 'prosemirror';

class AwarenessProvider {
  constructor(private awareness: Awareness) {}

  getAwareness() {
    return this.awareness;
  }
}

export default class EditorService extends Editor {
  id: string;
  resourceId: string;
  resourceType: string;
  parentResourceType: string;
  parentResourceId: string;
  activeButtonStates: any;
  disabledButtonStates: any;
  storeDispatch: any;
  eventBus: { $emit: (event: string, payload: any) => void };

  constructor(id: string, props?: EditorServiceOptions, options?: Partial<EditorOptions>) {
    const resourceId = props ? props.resourceId : '';
    const resourceType = props ? props.resourceType : '';
    const parentResourceType = props ? props.parentResourceType : '';
    const parentResourceId = props ? props.parentResourceId : '';
    const storeDispatch = props?.storeDispatch;
    const { displayImageFullScreen } = useDisplayImageFullScreen(storeDispatch);

    super({
      ...options,
      extensions: [
        ...(options?.extensions ? options.extensions : []),
        ...(props?.commenting
          ? [Comment.configure({ emitter: EventBus, getTextNodeFromPoint })]
          : []),
        ...(props?.highlight ? [PreCommentHighlight.extend({})] : []),
        CustomUserMention.extend({
          addNodeView() {
            return VueNodeViewRenderer(MentionNode);
          },
        }).configure({
          suggestion: userMentionSuggestion as any,
        }),
        EventHandler({
          emitter: EventBus,
          handleArrowLeft: props?.handleArrowLeft ?? false,
        }),
        Code.extend({
          excludes: '',
          exitable: true,
        }),
        ...(props?.allowImagesUpload
          ? [
              ImageUploadExtension({
                getRandId: () => nanoid(10),
                imageUploader,
                getFileCache,
              }).configure({
                editorId: id,
                resourceType: resourceType,
                parentResourceType: parentResourceType,
                parentResourceId: parentResourceId,
                upload: uploadFn,
                emitter: EventBus,
                preventInsertFiles: props?.preventInsertFiles,
              }),
            ]
          : []),
        InternalLink.extend({
          addNodeView() {
            return VueNodeViewRenderer(InternalLinkNode);
          },
          ...InternalLinkRenderExtend,
        }).configure({
          linkOnPaste: true,
          internalLinkRegEx: constants.internalLinkRegEx,
          plugins: [autolink],
          generateResourceData: getInternalLinkProps,
        }),
        Link.extend({
          addProseMirrorPlugins() {
            // get parent plugins and replace linkPaste plugin with custom one
            const parentPlugins = this.parent?.() ?? [];
            let plugins = replaceHandlePastePlugin(parentPlugins, this.editor, this.type, true);
            plugins = replaceLinkClickPlugin(EventBus, plugins, this.editor, this.type, true);
            return plugins;
          },
          inclusive: false,
        }).configure({
          autolink: true,
          linkOnPaste: true,
          validate: isNotInternalLink,
        }),
        MouseOver({
          linkTooltipComponent: LinkTooltip,
          blockHoverMenuComponent: BlockHoverMenu,
          getTextNodeFromPoint,
          draggable: props?.draggable,
        }),
        Placeholder.configure({
          placeholder: ({ editor }) => {
            if (editor.isFocused && props?.focusedStatePlaceholder) {
              return props.focusedStatePlaceholder;
            }
            return props ? (props.placeholder ?? '') : '';
          },
          emptyNodeClass: 'is-empty',
          emptyEditorClass: constants.emptyEditorClass,
          showOnlyWhenEditable: false,
        }),
        Resizing,
        StarterKit.configure({
          ...(!props?.heading && { heading: false }),
          dropcursor: {
            width: 4,
            color: '#498cf7',
            class: 'st-drop-cursor-block',
          },
          code: false,
          codeBlock: false,
          ...(props?.collaboration && { history: false }),
          horizontalRule: false,
          listItem: false,
          ...(!props?.lists && {
            bulletList: false,
            orderedList: false,
          }),
          blockquote: false,
          ...(!props?.blockElements && {
            codeBlock: false,
          }),
        }),
        ...(props?.lists
          ? [
              ListItem.extend({
                addKeyboardShortcuts() {
                  return {
                    Enter: () => {
                      if (this.editor.storage.mention.mentionDropdownActive) {
                        return false;
                      }
                      return this.editor.commands.splitListItem(this.name);
                    },
                    Tab: () => {
                      if (this.editor.storage.mention.mentionDropdownActive) {
                        return false;
                      }
                      return this.editor.commands.sinkListItem(this.name);
                    },
                    'Shift-Tab': () => this.editor.commands.liftListItem(this.name),
                  };
                },
              }),
              TaskList,
              TaskItem.configure({
                nested: true,
              }),
            ]
          : []),
        ...(props?.allowImages
          ? [
              StImage.extend({
                addNodeView() {
                  return VueNodeViewRenderer(MediaResize as Component);
                },
              }).configure({
                plugins: [doubleClick],
                unsplashBaseUrl: constants.UNSPLASH_BASE_URL,
                displayImageFullScreen,
                getFileIdFromUrl,
                unsplashObjectByImgSrc,
                objectByImgSrc,
                findChildren,
              }),
              StImagePlaceholder.extend({
                addNodeView: () => VueNodeViewRenderer(StImagePlaceholderNodeView),
              }).configure({
                getRandId: () => nanoid(6),
                stImageDefaultHeight: constants.stImageDefaultHeight,
                stImageDefaultWidth: constants.stImageDefaultWidth,
                corsProxyBaseUrl: constants.corsProxyBaseUrl,
              }),
            ]
          : []),
        Typography,
        Underline,
        ...(props?.draggable
          ? [
              UniqueId.configure({
                attributeName: constants.blockAttributeName,
                types: [
                  'heading',
                  'st-image',
                  'paragraph',
                  'bulletList',
                  'orderedList',
                  'taskList',
                  'codeBlock',
                  'blockquote',
                  'youtube',
                  'horizontalRule',
                  'table',
                  InlineMediaNode.Generic,
                  'callout',
                ],
                generateID: () => nanoid(6),
                filterTransaction: props.collaboration
                  ? (transaction) => !isChangeOrigin(transaction)
                  : undefined,
              }),
            ]
          : []),
        Youtube.extend({
          addNodeView: () => VueNodeViewRenderer(MediaResize as Component),
          addAttributes() {
            return {
              ...this.parent?.(),
              loading: {
                default: 'lazy',
              },
            };
          },
          addOptions() {
            return {
              ...this.parent?.(),
              tagName: 'iframe',
            };
          },
        }),
        ...(props?.collaboration
          ? [
              Collaboration.configure({
                document: props?.collaboration?.document,
                fragment: props?.collaboration?.document.getXmlFragment(ydocFragmentField),
              }),
              CollaborationCursor.configure({
                provider: new AwarenessProvider(props?.collaboration?.awareness),
                user: props?.collaboration?.user,
                render: renderCollaborationCursors,
              }),
            ]
          : []),
        PhantomPlugin.configure({
          resourceType,
        }),
        ...TablePlugins(resourceType as any),
        ...(props?.tableOfContent ? [TableOfContent.configure({})] : []),
        CustomEnter,
        GenericMediaNode,
        GenericMediaPlacholder,
        ...(props?.preventInsertFiles ? [FilterFiles] : []),
        ...(props?.characterLimit
          ? [
              CharacterCount.configure({
                limit: props.characterLimit,
                onLimitReached() {
                  EventBus.$emit('editorCharacterLimitReached', id);
                },
              }),
            ]
          : []),
        customShortcuts,
        ...(props?.textAlignment
          ? [
              TextAlign.configure({
                types: ['paragraph', 'heading'],
                alignments: ['left', 'center', 'right', 'justify'],
                defaultAlignment: 'left',
              }),
            ]
          : []),
        ...(props?.enableBoardEmbed
          ? [
              EmbedBoard.extend({
                addNodeView: () => VueNodeViewRenderer(BoardEmbed as any),
              }),
            ]
          : []),
        ...(props?.enableContentMenu ? [ContentMenu.configure({ resourceType })] : []),
        Callouts.extend({
          addNodeView: () => VueNodeViewRenderer(Callout as any),
          addOptions() {
            return {
              ...this.parent?.(),
              className: 'st-callout',
            };
          },
        }),
        CodeBlockLowlight.extend({
          addOptions() {
            return {
              ...this.parent?.(),
              tagName: 'pre',
              className: 'code-block',
            };
          },
          addKeyboardShortcuts() {
            return {
              ...this.parent?.(),
              ArrowUp: ({ editor }) => exitBlockAbove(editor, this.name),
            };
          },
          addNodeView: () => VueNodeViewRenderer(BlockNodeView as any),
        }).configure({
          lowlight: createLowlight(common),
          defaultLanguage: 'plaintext',
        }),
        ...(props?.blockElements
          ? [
              Blockquote.extend({
                addNodeView: () => VueNodeViewRenderer(BlockNodeView as any),
                addOptions() {
                  return {
                    ...this.parent?.(),
                    tagName: 'blockquote',
                    className: 'st-blockquote',
                  };
                },
              }),
            ]
          : []),
        ...(props?.horizontalRule
          ? [
              HorizontalRule.extend({
                addNodeView: () => VueNodeViewRenderer(BlockNodeView as any),
                addOptions() {
                  return {
                    ...this.parent?.(),
                    tagName: 'hr',
                    className: 'st-horizontal-rule',
                  };
                },
              }),
            ]
          : []),
      ],
    });

    this.id = id;
    this.resourceId = resourceId;
    this.resourceType = resourceType;
    this.parentResourceType = parentResourceType;
    this.parentResourceId = parentResourceId;
    this.storeDispatch = storeDispatch;
    this.eventBus = EventBus;
    this.activeButtonStates = reactive<Record<string, boolean>>({
      heading: false,
      h1: false,
      h2: false,
      h3: false,
      h4: false,
      h5: false,
      bold: false,
      italic: false,
      underline: false,
      strike: false,
      textAlignLeft: false,
      textAlignCenter: false,
      textAlignRight: false,
      textAlignJustified: false,
      link: false,
      blockquote: false,
      code: false,
      taskList: false,
      bulletList: false,
      orderedList: false,
      paragraph: false,
    });

    this.disabledButtonStates = reactive<Record<string, boolean>>({
      undo: true,
      redo: true,
      isInTable: false,
      mergeCells: false,
    });

    const ACTIVE_STATES_UPDATE_DELAY = 100;
    let updateTimeout: any = null;

    const onEditorUpdated = ({ editor }: { editor: any }) => {
      if (updateTimeout) {
        clearTimeout(updateTimeout);
      }
      updateTimeout = setTimeout(() => this.updateButtonStates(editor), ACTIVE_STATES_UPDATE_DELAY);
    };

    this.on('update', onEditorUpdated);
    this.on('selectionUpdate', onEditorUpdated);
  }

  updateButtonStates(editor: Editor) {
    this.activeButtonStates.heading = editor.isActive('heading');
    this.activeButtonStates.h1 = editor.isActive('heading', { level: 1 });
    this.activeButtonStates.h2 = editor.isActive('heading', { level: 2 });
    this.activeButtonStates.h3 = editor.isActive('heading', { level: 3 });
    this.activeButtonStates.h4 = editor.isActive('heading', { level: 4 });

    this.activeButtonStates.bold = editor.isActive('bold');
    this.activeButtonStates.italic = editor.isActive('italic');
    this.activeButtonStates.underline = editor.isActive('underline');
    this.activeButtonStates.strike = editor.isActive('strike');

    this.activeButtonStates.paragraph = editor.isActive('paragraph');

    this.activeButtonStates.textAlignLeft = editor.isActive({ textAlign: 'left' });
    this.activeButtonStates.textAlignCenter = editor.isActive({ textAlign: 'center' });
    this.activeButtonStates.textAlignRight = editor.isActive({ textAlign: 'right' });
    this.activeButtonStates.textAlignJustified = editor.isActive({ textAlign: 'justify' });

    this.activeButtonStates.link = editor.isActive('link');
    this.activeButtonStates.blockquote = editor.isActive('blockquote');
    this.activeButtonStates.code = editor.isActive('code');

    this.activeButtonStates.taskList = editor.isActive('taskList');
    this.activeButtonStates.bulletList = editor.isActive('bulletList');
    this.activeButtonStates.orderedList = editor.isActive('orderedList');

    // disabled button states
    this.disabledButtonStates.undo = !editor.can().chain().focus().undo().run();
    this.disabledButtonStates.redo = !editor.can().chain().focus().redo().run();

    // table button states
    this.disabledButtonStates.isInTable = isInTable(editor.state);
    this.disabledButtonStates.mergeCells = mergeCellsDisabled(editor.state);
  }

  updateEditorId(id: string) {
    this.id = id;

    // update editor id on the root dom node
    const root = this.view.dom;
    root.setAttribute('id', id);
  }

  updateEditorResourceId(resourceId: string) {
    this.resourceId = resourceId;
  }

  public updateEditorContentClass(className: string) {
    this.view.dom.className = `ProseMirror ${className}`;
  }

  public addEditorContentClass(className: string) {
    this.view.dom.classList.add(className);
  }

  public removeEditorContentClass(className: string) {
    this.view.dom.classList.remove(className);
  }
}
